import React from "react";
import { Box, Typography } from "@mui/material";

const INK = "#0f1121";
const INK_SOFT = "rgba(15, 17, 33, 0.62)";
const INK_FAINT = "rgba(15, 17, 33, 0.42)";
const INK_HAIRLINE = "rgba(15, 17, 33, 0.08)";
const ACCENT = "#3854e8";
const SURFACE = "#ffffff";

const MONO =
  "'JetBrains Mono', 'Söhne Mono', ui-monospace, SFMono-Regular, Menlo, monospace";
const SANS = "'sohne-var', 'Helvetica Neue', 'Arial', sans-serif";

const DAILY_GOAL = 5;

const Stat = ({ label, value, hint, fillPct, accent }) => (
  <Box sx={{ flex: 1, py: 2.5, px: { xs: 2.5, sm: 3 } }}>
    <Typography
      sx={{
        fontFamily: MONO,
        fontSize: "0.7rem",
        fontWeight: 500,
        letterSpacing: "0.16em",
        textTransform: "uppercase",
        color: INK_FAINT,
        mb: 1.5
      }}
    >
      {label}
    </Typography>
    <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.25, mb: 1.75 }}>
      <Typography
        sx={{
          fontFamily: MONO,
          fontSize: { xs: "2.4rem", md: "2.8rem" },
          fontWeight: 500,
          lineHeight: 1,
          color: INK,
          fontVariantNumeric: "tabular-nums",
          letterSpacing: "-0.02em"
        }}
      >
        {value}
      </Typography>
      {hint && (
        <Typography
          sx={{
            fontFamily: SANS,
            fontSize: "0.85rem",
            color: INK_SOFT,
            lineHeight: 1.3
          }}
        >
          {hint}
        </Typography>
      )}
    </Box>
    <Box
      sx={{
        height: 4,
        borderRadius: "999px",
        backgroundColor: INK_HAIRLINE,
        position: "relative",
        overflow: "hidden"
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          bottom: 0,
          width: `${Math.max(2, Math.min(100, fillPct))}%`,
          backgroundColor: accent || INK,
          transition: "width 0.6s cubic-bezier(0.16, 1, 0.3, 1)"
        }}
      />
    </Box>
  </Box>
);

const ProgressStatistics = ({ totalQuestions = 0, questionsToday = 0 }) => {
  const totalFill = Math.min(100, (totalQuestions / 100) * 100);
  const todayFill = Math.min(100, (questionsToday / DAILY_GOAL) * 100);
  return (
    <Box
      sx={{
        backgroundColor: SURFACE,
        borderRadius: "24px",
        border: `1px solid ${INK_HAIRLINE}`,
        boxShadow:
          "inset 0 1px 0 rgba(255, 255, 255, 0.6), 0 24px 60px -32px rgba(15, 17, 33, 0.18), 0 2px 6px -2px rgba(15, 17, 33, 0.05)",
        textAlign: "left",
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }
      }}
    >
      <Stat
        label="Total answered"
        value={totalQuestions}
        hint="all-time"
        fillPct={totalFill}
        accent={INK}
      />
      <Box
        sx={{
          borderLeft: { sm: `1px solid ${INK_HAIRLINE}` },
          borderTop: { xs: `1px solid ${INK_HAIRLINE}`, sm: "none" }
        }}
      >
        <Stat
          label="Practiced today"
          value={questionsToday}
          hint={`of ${DAILY_GOAL} daily goal`}
          fillPct={todayFill}
          accent={ACCENT}
        />
      </Box>
    </Box>
  );
};

export default ProgressStatistics;
